import { Button } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { api, unwrap } from '../../api/client';
import { foodpandaKeys, useFoodpanda } from './api';

/** A short two-tone chime (no audio file); browsers stay silent until the page was clicked once. */
function chime() {
  try {
    const ctx = new AudioContext();
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      const at = ctx.currentTime + i * 0.18;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.3, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.16);
      osc.connect(gain).connect(ctx.destination);
      osc.start(at);
      osc.stop(at + 0.17);
    });
    window.setTimeout(() => void ctx.close(), 800);
  } catch {
    // No audio available: the pop-up still shows.
  }
}

/**
 * Anywhere in the app: a chime and a pop-up when a new Foodpanda order arrives, for staff who
 * handle orders. Orders already waiting when the page opened do not ring.
 */
export function FoodpandaAlerts() {
  const fp = useFoodpanda();
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const seen = useRef<Set<string> | null>(null);
  const on = fp.visible && fp.canOrder;
  const waiting = useQuery({
    queryKey: [...foodpandaKeys.orders({ status: 'RECEIVED' }), 'alerts'],
    queryFn: () =>
      unwrap(
        api.GET('/foodpanda/orders', { params: { query: { status: 'RECEIVED', pageSize: 50 } } }),
      ),
    enabled: on,
    refetchInterval: 15_000,
  });

  useEffect(() => {
    const rows = waiting.data?.data;
    if (!rows) return;
    if (!seen.current) {
      seen.current = new Set(rows.map((o) => o.id));
      return;
    }
    const fresh = rows.filter((o) => !seen.current?.has(o.id));
    for (const o of fresh) seen.current.add(o.id);
    if (!fresh.length) return;
    chime();
    enqueueSnackbar(
      fresh.length === 1
        ? `New Foodpanda order #${fresh[0]?.code ?? ''}`
        : `${String(fresh.length)} new Foodpanda orders`,
      {
        variant: 'info',
        persist: true,
        action: (key) => (
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              closeSnackbar(key);
              void navigate('/foodpanda');
            }}
          >
            Open
          </Button>
        ),
      },
    );
  }, [waiting.data, enqueueSnackbar, closeSnackbar, navigate]);

  return null;
}
